import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Mail, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, UserPlus, CheckSquare, Activity, Clock, ArrowRight, FileText, Settings, Eye, Reply, LayoutGrid } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [dailyStats, setDailyStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [domain, setDomain] = useState('');
    const [reputation, setReputation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [latestTask, setLatestTask] = useState(null);
    const [stages, setStages] = useState([]);

    useEffect(() => {
        fetchStats();
        fetchDailyStats();
        fetchRecentActivity();
        fetchChartData();
        fetchLatestTask();
        fetchStages();
    }, []);

    const fetchStages = async () => {
        try {
            const response = await axios.get(`${API_URL}/pipeline/stages`);
            setStages(response.data);
        } catch (error) {
            console.error('Failed to fetch stages:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API_URL}/stats`);
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to fetch stats');
        }
    };

    const fetchDailyStats = async () => {
        try {
            // Get timezone offset in minutes
            const timezoneOffset = -new Date().getTimezoneOffset();
            const response = await axios.get(`${API_URL}/stats/daily`, {
                params: { timezone_offset: timezoneOffset }
            });
            setDailyStats(response.data);
        } catch (error) {
            toast.error('Failed to fetch daily stats');
        }
    };

    const fetchRecentActivity = async () => {
        try {
            const response = await axios.get(`${API_URL}/activity/recent`, {
                params: { limit: 10 }
            });
            setRecentActivity(response.data);
        } catch (error) {
            toast.error('Failed to fetch recent activity');
        }
    };

    const fetchChartData = async () => {
        try {
            const response = await axios.get(`${API_URL}/stats/chart`);
            setChartData(response.data);
        } catch (error) {
            toast.error('Failed to fetch chart data');
        }
    };

    const fetchLatestTask = async () => {
        try {
            const response = await axios.get(`${API_URL}/tasks/my-latest`);
            // Check if response.data exists and is not null/empty
            if (response.data && response.data !== null && typeof response.data === 'object' && Object.keys(response.data).length > 0 && response.data.id) {
                setLatestTask(response.data);
            } else {
                // No task found - set to empty object to show empty state
                setLatestTask({ empty: true });
            }
        } catch (error) {
            // Handle 404 or other errors - show empty state
            if (error.response && error.response.status === 404) {
                setLatestTask({ empty: true });
            } else {
                console.error('Error fetching latest task:', error);
                setLatestTask({ empty: true });
            }
        }
    };

    const checkReputation = async () => {
        if (!domain) {
            toast.error('Please enter a domain');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/reputation/${domain}`);
            setReputation(response.data);
        } catch (error) {
            toast.error('Failed to check domain reputation');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
                return 'text-green-600 bg-green-50';
            case 'warning':
                return 'text-amber-600 bg-amber-50';
            case 'critical':
                return 'text-red-600 bg-red-50';
            default:
                return 'text-slate-600 bg-slate-50';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy':
                return <CheckCircle className="h-5 w-5" />;
            case 'warning':
                return <AlertTriangle className="h-5 w-5" />;
            case 'critical':
                return <AlertTriangle className="h-5 w-5" />;
            default:
                return null;
        }
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'email':
                return <Mail className="h-4 w-4 text-purple-600" />;
            case 'lead':
                return <UserPlus className="h-4 w-4 text-blue-600" />;
            case 'task':
                return <CheckSquare className="h-4 w-4 text-green-600" />;
            case 'template':
                return <FileText className="h-4 w-4 text-indigo-600" />;
            case 'account':
                return <Settings className="h-4 w-4 text-amber-600" />;
            case 'pipeline_stage':
                return <LayoutGrid className="h-4 w-4 text-teal-600" />;
            default:
                return <Activity className="h-4 w-4 text-slate-600" />;
        }
    };

    const formatTimeAgo = (timestamp) => {
        if (timestamp == null) return 'Unknown time';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid time';
        const now = new Date();
        const diffMs = now - date;

        // Handle future timestamps
        if (diffMs < 0) {
            const futureDiffMs = Math.abs(diffMs);
            const futureDiffMins = Math.floor(futureDiffMs / 60000);
            const futureDiffHours = Math.floor(futureDiffMs / 3600000);
            const futureDiffDays = Math.floor(futureDiffMs / 86400000);

            if (futureDiffMins < 1) return 'Just now';
            if (futureDiffMins < 60) return `in ${futureDiffMins}m`;
            if (futureDiffHours < 24) return `in ${futureDiffHours}h`;
            if (futureDiffDays < 7) return `in ${futureDiffDays}d`;
            return date.toLocaleDateString();
        }

        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleActivityClick = (activity) => {
        if (activity.lead_id) {
            navigate(`/leads/${activity.lead_id}`);
        } else if (activity.type === 'pipeline_stage') {
            navigate('/pipeline');
        }
    };

    const handleTaskClick = () => {
        if (latestTask && latestTask.lead_id) {
            navigate(`/leads/${latestTask.lead_id}`);
        }
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return 'No due date';
        const date = new Date(dueDate);
        if (isNaN(date.getTime())) return 'Invalid date';

        const now = new Date();
        const diffMs = date - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
        } else if (diffDays === 0) {
            return 'Due today';
        } else if (diffDays === 1) {
            return 'Due tomorrow';
        } else if (diffDays < 7) {
            return `Due in ${diffDays} days`;
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
    };

    const getTaskTypeColor = (type) => {
        switch (type) {
            case 'Call':
                return 'bg-blue-100 text-blue-700';
            case 'Email':
                return 'bg-purple-100 text-purple-700';
            case 'LinkedIn':
                return 'bg-indigo-100 text-indigo-700';
            case 'Todo':
                return 'bg-slate-100 text-slate-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getTaskDueDateStatus = (dueDate) => {
        if (!dueDate) return 'later'; // No due date = treat as later

        const date = new Date(dueDate);
        if (isNaN(date.getTime())) return 'later';

        const now = new Date();
        // Reset time to midnight for accurate day comparison
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const due = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return 'overdue'; // Overdue
        } else if (diffDays === 0) {
            return 'today'; // Due today
        } else {
            return 'later'; // Due later
        }
    };

    const getTaskCardColors = (dueDate) => {
        const status = getTaskDueDateStatus(dueDate);

        switch (status) {
            case 'overdue':
                return {
                    border: 'border-red-300',
                    background: 'bg-gradient-to-r from-red-50 to-rose-50',
                    iconBg: 'bg-red-100',
                    iconColor: 'text-red-700',
                    arrowColor: 'text-red-700'
                };
            case 'today':
                return {
                    border: 'border-orange-300',
                    background: 'bg-gradient-to-r from-orange-50 to-amber-50',
                    iconBg: 'bg-orange-100',
                    iconColor: 'text-orange-700',
                    arrowColor: 'text-orange-700'
                };
            case 'later':
            default:
                return {
                    border: 'border-green-300',
                    background: 'bg-gradient-to-r from-green-50 to-emerald-50',
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-700',
                    arrowColor: 'text-green-700'
                };
        }
    };

    return (
        <div className="p-6 md:p-8 lg:p-10 space-y-8" data-testid="dashboard-page">
            <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Dashboard</h1>
                <p className="text-sm leading-relaxed text-slate-600">Overview of your team's outreach performance</p>
            </div>

            {/* Overview Section */}
            {stats && (
                <div className="space-y-4">
                    <h2 className="text-2xl font-semibold text-slate-900">Overview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-blue-50">
                                    <Users className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Leads</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">{stats.total_leads}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-purple-50">
                                    <Mail className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Emails</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">{stats.total_emails}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-green-50">
                                    <TrendingUp className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Closed Deals</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {stats.closed_deals_count ?? stats.leads_by_status?.Closed ?? 0}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-amber-50">
                                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">In Conversation</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {stats.leads_by_status?.['In Conversation'] || 0}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-indigo-50">
                                    <Eye className="h-6 w-6 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Open Rate</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {stats.open_rate !== undefined ? `${stats.open_rate}%` : '0%'}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-cyan-50">
                                    <Reply className="h-6 w-6 text-cyan-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Reply Rate</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {stats.reply_rate !== undefined ? `${stats.reply_rate}%` : '0%'}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* Today's Pulse Section */}
            {dailyStats && (
                <div className="space-y-4">
                    <h2 className="text-2xl font-semibold text-slate-900">Today</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-green-50">
                                        <UserPlus className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase tracking-wider">New Leads</p>
                                        <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                            {dailyStats.leads_today}
                                        </p>
                                    </div>
                                </div>
                                {dailyStats.leads_yesterday !== undefined && (
                                    <Badge
                                        variant={dailyStats.leads_today >= dailyStats.leads_yesterday ? 'default' : 'secondary'}
                                        className={
                                            dailyStats.leads_today >= dailyStats.leads_yesterday
                                                ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                : ''
                                        }
                                    >
                                        {dailyStats.leads_today >= dailyStats.leads_yesterday ? '+' : ''}
                                        {dailyStats.leads_today - dailyStats.leads_yesterday} vs yesterday
                                    </Badge>
                                )}
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-purple-50">
                                    <Mail className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Emails Sent</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {dailyStats.emails_sent_today}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-indigo-50">
                                    <Eye className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Open Rate</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {dailyStats.open_rate_today !== undefined ? `${dailyStats.open_rate_today}%` : '0%'}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-cyan-50">
                                    <Reply className="h-5 w-5 text-cyan-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Reply Rate</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {dailyStats.reply_rate_today !== undefined ? `${dailyStats.reply_rate_today}%` : '0%'}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-50">
                                    <CheckSquare className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Tasks Done</p>
                                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                                        {dailyStats.tasks_completed_today}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* My Latest Task */}
            {latestTask && (() => {
                const colors = latestTask.empty
                    ? { border: 'border-slate-200', background: 'bg-slate-50', iconBg: 'bg-slate-100', iconColor: 'text-slate-400' }
                    : getTaskCardColors(latestTask.due_date);

                return (
                    <Card
                        className={`rounded-xl border-2 ${colors.border} ${colors.background} shadow-sm p-6 ${latestTask.id && latestTask.lead_id ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                        onClick={latestTask.id && latestTask.lead_id ? handleTaskClick : undefined}
                    >
                        {latestTask.empty ? (
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-lg ${colors.iconBg}`}>
                                    <CheckSquare className={`h-6 w-6 ${colors.iconColor}`} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-slate-900 mb-1">My Latest Task</h3>
                                    <p className="text-sm text-slate-500">No pending tasks assigned to you. You're all caught up! 🎉</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className={`p-3 rounded-lg ${colors.iconBg}`}>
                                        <CheckSquare className={`h-6 w-6 ${colors.iconColor}`} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="text-lg font-semibold text-slate-900">My Latest Task</h3>
                                            {latestTask.type && (
                                                <Badge className={getTaskTypeColor(latestTask.type)}>
                                                    {latestTask.type}
                                                </Badge>
                                            )}
                                        </div>
                                        {latestTask.description && (
                                            <p className="text-sm text-slate-700 mb-2">{latestTask.description}</p>
                                        )}
                                        {latestTask.lead_name && (
                                            <p className="text-xs text-slate-600 mb-2">
                                                Lead: <span className="font-medium">{latestTask.lead_name}</span>
                                            </p>
                                        )}
                                        {latestTask.due_date && (
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <Clock className="h-3 w-3" />
                                                <span>{formatDueDate(latestTask.due_date)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {latestTask.lead_id && (
                                    <div className={`flex items-center ${colors.arrowColor}`}>
                                        <ArrowRight className="h-5 w-5" />
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                );
            })()}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Stats and Charts */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Leads by Status */}
                    {stats && (
                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <h2 className="text-xl font-medium text-slate-900 mb-4">Leads by Status</h2>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                {stages.length > 0
                                    ? stages.map((stage) => (
                                        <div key={stage.id} className="text-center p-4 rounded-lg bg-slate-50">
                                            <p className="text-2xl font-bold text-slate-900">
                                                {stats.leads_by_status?.[stage.id] ?? 0}
                                            </p>
                                            <p className="text-xs text-slate-600 mt-1">{stage.name}</p>
                                        </div>
                                    ))
                                    : Object.entries(stats.leads_by_status || {}).map(([statusId, count]) => (
                                        <div key={statusId} className="text-center p-4 rounded-lg bg-slate-50">
                                            <p className="text-2xl font-bold text-slate-900">{count}</p>
                                            <p className="text-xs text-slate-600 mt-1 truncate" title={statusId}>
                                                {stages.find(s => s.id === statusId)?.name || statusId}
                                            </p>
                                        </div>
                                    ))
                                }
                            </div>
                        </Card>
                    )}

                    {/* Activity Volume Chart */}
                    {chartData.length > 0 && (
                        <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                            <h2 className="text-xl font-medium text-slate-900 mb-4">Activity Volume (Last 7 Days)</h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="emails" fill="#8b5cf6" name="Emails Sent" />
                                    <Bar dataKey="leads" fill="#10b981" name="Leads Added" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    )}

                    {/* Domain Reputation Monitor */}
                    <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                        <h2 className="text-xl font-medium text-slate-900 mb-4">Domain Reputation Monitor</h2>
                        <p className="text-sm text-slate-600 mb-4">
                            Check SPF and DMARC records for your sending domains
                        </p>

                        <div className="flex gap-2 mb-6">
                            <Input
                                data-testid="domain-input"
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="example.com"
                                className="flex-1 rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                            />
                            <Button
                                data-testid="check-reputation-button"
                                onClick={checkReputation}
                                disabled={loading}
                                className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Check'}
                            </Button>
                        </div>

                        {reputation && (
                            <div
                                data-testid="reputation-result"
                                className={`p-4 rounded-lg border ${getStatusColor(reputation.status)} border-current`}
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    {getStatusIcon(reputation.status)}
                                    <div>
                                        <p className="font-semibold text-sm">{reputation.domain}</p>
                                        <p className="text-xs capitalize">{reputation.status}</p>
                                    </div>
                                </div>
                                <p className="text-sm mb-3">{reputation.message}</p>
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                    <div>
                                        <p className="font-medium mb-1">SPF Record:</p>
                                        <p className={reputation.has_spf ? 'text-green-700' : 'text-red-700'}>
                                            {reputation.has_spf ? '✓ Valid' : '✗ Not Found'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1">DMARC Record:</p>
                                        <p className={reputation.has_dmarc ? 'text-green-700' : 'text-red-700'}>
                                            {reputation.has_dmarc ? '✓ Valid' : '✗ Not Found'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Right Column - Recent Activity */}
                <div className="lg:col-span-1">
                    <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                        <h2 className="text-xl font-medium text-slate-900 mb-4">Recent Activity</h2>
                        <div className="space-y-3">
                            {recentActivity.length > 0 ? (
                                recentActivity.map((activity, index) => (
                                    <div
                                        key={`${activity.type}-${activity.id}-${index}`}
                                        onClick={() => handleActivityClick(activity)}
                                        className={`p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors ${(activity.lead_id || activity.type === 'pipeline_stage') ? 'cursor-pointer' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-slate-900">
                                                    <span className="font-medium">{activity.user_email || 'Unknown'}</span>{' '}
                                                    {activity.description}
                                                </p>
                                                {activity.lead_name && (
                                                    <p className="text-xs text-slate-500 mt-1 truncate">
                                                        Lead: {activity.lead_name}
                                                    </p>
                                                )}
                                                <p className="text-xs text-slate-400 mt-1">
                                                    {formatTimeAgo(activity.timestamp)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
